import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import BranchLocationModal from "../components/BranchLocationModal";
import BranchStatusBadge from "../components/BranchStatusBadge";
import GenderBadge from "../components/GenderBadge";
import useBranchUsage from "../hooks/useBranchUsage";
import { useListUrlState, useScrollRestoration } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { can } from "../services/permissions";
import { activeLimitReached, fetchBranches } from "../services/branchService";


const NO_FILTERS = {};
export default function Branches() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // Opening a branch is a permission, not a role: any role the business gave
  // branches.create may do it.
  const canCreate = can(getCurrentUser(), "branches.create");
  // Editing is its own permission: a branch manager may not open a branch but
  // may keep their own one's details right.
  const canEdit = can(getCurrentUser(), "branches.update");

  const [branches, setBranches] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  // The page number lives in the URL, so Back from a branch returns to it.
  const { page, setPage } = useListUrlState(NO_FILTERS);
  useScrollRestoration(!loading);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(location.state?.flash ?? "");
  // The row being edited, or null when the modal is closed. The row itself is
  // the form's source: the list endpoint returns the same full BranchResource
  // the detail page gets, so nothing has to be refetched to open it.
  const [editing, setEditing] = useState(null);
  // Active branches against the plan's cap — the cap is on active, not total.
  const { usage, reload: reloadUsage } = useBranchUsage();

  // Consume the flash so a refresh does not show it again.
  useEffect(() => {
    if (location.state?.flash) navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { branches: list, meta: pagination } = await fetchBranches(page);
      setBranches(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("branches.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [page, i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("branches.title")}</h1>
          <p className="text-sm text-ink-2">
            {meta ? t("branches.count", { count: meta.total }) : t("branches.subtitle")}
          </p>
          {usage && (
            <p className="text-sm text-ink-2">
              {usage.branches_active_limit == null
                ? t("branches.activeUsageUnlimited", { count: usage.branches_active_count })
                : t("branches.activeUsage", {
                    count: usage.branches_active_count,
                    limit: usage.branches_active_limit,
                  })}
            </p>
          )}
        </div>
        {/* Creating a branch is owner-only. */}
        {canCreate && (
          <Link to="/branches/create" className="btn-primary">
            {t("branches.new")}
          </Link>
        )}
      </div>

      {flash && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-accent-line bg-accent-wash px-3 py-2 text-sm text-accent-text">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash("")} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {activeLimitReached(usage) && (
        <div className="mb-4 rounded-lg border border-line bg-raised px-3 py-2 text-sm text-ink-2">
          {t("branches.activeLimitNote", { limit: usage.branches_active_limit })}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : branches.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-ink-2">
              {canCreate ? t("branches.empty") : t("branches.emptyManager")}
            </p>
            {canCreate && (
              <Link to="/branches/create" className="btn-primary mt-3 inline-block">
                {t("branches.createFirst")}
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("branches.table.branch")}</th>
                  <th className="px-4 py-3">{t("branches.table.status")}</th>
                  <th className="px-4 py-3">{t("branches.table.city")}</th>
                  <th className="px-4 py-3">{t("branches.table.gender")}</th>
                  <th className="px-4 py-3">{t("branches.table.manager")}</th>
                  <th className="px-4 py-3">{t("branches.table.businessTypes")}</th>
                  <th className="px-4 py-3">{t("branches.table.currency")}</th>
                  <th className="px-4 py-3">{t("branches.table.media")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {branches.map((branch) => (
                  <tr key={branch.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {branch.logo && (
                          <img
                            src={branch.logo}
                            alt=""
                            className="h-8 w-8 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium">{branch.name}</p>
                          <p className="text-xs text-ink-2">{branch.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <BranchStatusBadge status={branch.status} />
                    </td>
                    <td className="px-4 py-3 text-ink-2">{branch.city?.name ?? "—"}</td>
                    <td className="px-4 py-3">
                      <GenderBadge gender={branch.gender} />
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {branch.manager ? branch.manager.name : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(branch.business_types ?? []).map((type) => (
                          <span
                            key={type.id}
                            className="rounded-full bg-raised px-2 py-1 text-xs text-ink"
                          >
                            {type.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-2">{branch.currency?.code ?? "—"}</td>
                    <td className="px-4 py-3 text-ink-2">{branch.media?.length ?? 0}</td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <Link
                          to={`/branches/${branch.id}/services`}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("services.title")}
                        </Link>
                        <Link
                          to={`/branches/${branch.id}/products`}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("products.title")}
                        </Link>
                        {/* The same modal the detail page uses, opened on the
                            row so the common edit — a phone, an address, the
                            status — does not cost two navigations. */}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => setEditing(branch)}
                            className="text-sm font-medium text-accent-text hover:underline"
                          >
                            {t("common.edit")}
                          </button>
                        )}
                        <Link
                          to={`/branches/${branch.id}`}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.view")}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* `branch` is null while closed, and the modal renders nothing then. */}
      <BranchLocationModal
        branch={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        // The Google place saves on its own, before the rest of the form.
        onChanged={(updated) =>
          setBranches((list) => list.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)))
        }
        onSaved={(updated) => {
          setEditing(null);
          // Patch the row in place rather than reloading the page: a refetch
          // would lose the reader's scroll position, and the API hands back the
          // full branch it just saved.
          setBranches((list) =>
            list.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
          );
          // The edited row may be off-screen on a long list, so say so rather
          // than relying on the reader spotting the change.
          setFlash(t("branchForm.updated", { name: updated.name }));
          // A status change moves the active count.
          reloadUsage();
        }}
      />

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}
    </div>
  );
}
